import React, { useCallback, useEffect, useState } from 'react';
import {
  Pagination,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, Search, Sync, Add, Visibility, SwapHoriz, CalendarToday, Home } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import AppBarComponent from '../../../components/AppBarComponent';
import RealocacaoDetailDialog from '../../../components/RealocacaoDetailDialog';

// Constantes para paginação
const DEFAULT_PAGE_SIZE = 10;
const PAGINATION_OPTIONS = [10, 25, 50];

// Opções de status
const statusOptions = [
  { value: 'created', label: 'Criada', color: 'warning' },
  { value: 'in_progress', label: 'Em Andamento', color: 'info' },
  { value: 'concluded', label: 'Concluída', color: 'success' },
  { value: 'canceled', label: 'Cancelada', color: 'error' }
];

// Tipos de imóvel
const propertyTypes = [
  { value: 'APARTAMENTO', label: 'Apartamento' },
  { value: 'CASA', label: 'Casa' },
  { value: 'COMERCIAL', label: 'Comercial' },
  { value: 'TERRENO', label: 'Terreno' }
];

// Componentes estilizados
const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const StatusBadge = styled(Chip)(({ theme, $statusvalue }) => {
  const status = statusOptions.find(s => s.value === $statusvalue);
  return {
    backgroundColor: status?.color ? theme.palette[status.color]?.light : theme.palette.grey[300],
    color: status?.color ? theme.palette[status.color]?.contrastText : theme.palette.text.primary,
    fontWeight: 'bold',
    borderRadius: theme.shape.borderRadius,
  };
});

const PropertyTypeBadge = styled(Chip)(({ theme }) => ({
  fontWeight: '500',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  '& .MuiOutlinedInput-root': {
    height: '56px',
    fontSize: '1rem',
    borderRadius: theme.shape.borderRadius,
    paddingLeft: theme.spacing(1),
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(44px, 18px) scale(1)',
    '&.Mui-focused': {
      transform: 'translate(14px, -9px) scale(0.75)',
      color: theme.palette.primary.main,
    },
    '&.MuiFormLabel-filled': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputAdornment-root': {
    marginRight: theme.spacing(1),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
  },
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  '& svg': {
    fontSize: '4rem',
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
}));

export default function GerenciamentoRealocacoesPage() {
  const [realocacoes, setRealocacoes] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [selectedRealocacao, setSelectedRealocacao] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const navigate = useNavigate();

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8087/';
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    return new URL(endpoint, normalizedBase).toString();
  };

  const fetchRealocacoes = useCallback(async (page, search = '') => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || '';

      if (!token) {
        throw new Error('Usuário não autenticado');
      }

      const params = {
        page: page,
        size: pagination.pageSize,
      };

      if (search && search.trim() !== '') {
        params.searchTerm = search.trim();
      }

      const url = buildApiUrl('realocation');
      const response = await axios.get(url, {
        params,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.data?.data) {
        throw new Error('Estrutura de dados inválida na resposta');
      }

      setRealocacoes(response.data.data);
      setPagination({
        currentPage: response.data.meta.currentPage,
        totalPages: response.data.meta.totalPages,
        totalItems: response.data.meta.totalItems,
        pageSize: response.data.meta.pageSize
      });
    } catch (error) {
      console.error('Erro ao carregar realocações:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao carregar realocações. Verifique sua conexão e tente novamente.';

      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    fetchRealocacoes(pagination.currentPage, appliedSearchTerm);
  }, [pagination.currentPage, appliedSearchTerm, fetchRealocacoes]);

  const handlePageChange = (_, page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (e) => {
    const newSize = e.target.value;
    setPagination(prev => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1
    }));
  };

  const handleEdit = (realocacao) => {
    if (realocacao.status === 'concluded' || realocacao.status === 'cancelled') {
      setSnackbar({
        open: true,
        message: `Não é possível editar realocações ${realocacao.status === 'concluded' ? 'concluídas' : 'canceladas'}`,
        severity: 'warning',
      });
      return;
    }

    const realocacaoData = {
      ...realocacao,
      cost: realocacao.cost || 0,
      startDate: realocacao.startDate?.split('T')[0] || '',
      expectedEndDate: realocacao.expectedEndDate?.split('T')[0] || '',
      originAddress: {
        street: realocacao.originAddress?.street || '',
        city: realocacao.originAddress?.city || '',
        neighborhood: realocacao.originAddress?.neighborhood || '',
        state: realocacao.originAddress?.state || '',
        country: realocacao.originAddress?.country || 'Brasil'
      },
      destinationAddress: {
        street: realocacao.destinationAddress?.street || '',
        city: realocacao.destinationAddress?.city || '',
        neighborhood: realocacao.destinationAddress?.neighborhood || '',
        state: realocacao.destinationAddress?.state || '',
        country: realocacao.destinationAddress?.country || 'Brasil'
      },
      propertyType: realocacao.propertyType || '',
      category: realocacao.category || '',
      petFriendly: Boolean(realocacao.petFriendly),
      observation: realocacao.observation || ''
    };

    navigate(`/realocation/editar/${realocacao.id}`, {
      state: { realocacaoData }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.street || ''}, ${address.number || 'S/N'}`.trim();
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDetails = (realocacao) => {
    setSelectedRealocacao(realocacao);
    setDetailOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailOpen(false);
    setSelectedRealocacao(null);
  };

  return (
    <section>
      <AppBarComponent title="Gerenciamento de Realocações" />
      <Container elevation={0}>
        <SearchField
          fullWidth
          variant="outlined"
          label="Buscar realocações"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <EmptyState>
            <Typography color="error" variant="h6" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => fetchRealocacoes(pagination.currentPage, appliedSearchTerm)}
              startIcon={<Sync />}
            >
              Tentar novamente
            </Button>
          </EmptyState>
        ) : realocacoes.length === 0 ? (
          <EmptyState>
            <SwapHoriz fontSize="inherit" />
            <Typography variant="h6" color="textSecondary">
              {appliedSearchTerm
                ? 'Nenhuma realocação encontrada com esse critério de busca.'
                : 'Nenhuma realocação cadastrada.'}
            </Typography>
            {!appliedSearchTerm && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate('/relocations/new')}
                startIcon={<Add />}
              >
                Adicionar primeira realocação
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" color="textSecondary">
                Mostrando {realocacoes.length} de {pagination.totalItems} realocações
              </Typography>

              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Itens por página:
                </Typography>
                <Select
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  {PAGINATION_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tipo Imóvel</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Data Início</TableCell>
                    <TableCell>Data Prevista</TableCell>
                    <TableCell width="120px" align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {realocacoes.map((realocacao) => (
                    <TableRow key={realocacao.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {realocacao.titleRelocation || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          label={statusOptions.find(s => s.value === realocacao.status)?.label || realocacao.status}
                          $statusvalue={realocacao.status}
                        />
                      </TableCell>
                      <TableCell>
                        <PropertyTypeBadge
                          label={propertyTypes.find(t => t.value === realocacao.propertyType)?.label || realocacao.propertyType}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Home color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatAddress(realocacao.originAddress)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {realocacao.originAddress?.city || 'N/A'}, {realocacao.originAddress?.state || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Home color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatAddress(realocacao.destinationAddress)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {realocacao.destinationAddress?.city || 'N/A'}, {realocacao.destinationAddress?.state || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatDate(realocacao.startDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatDate(realocacao.expectedEndDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Visualizar detalhes">
                            <ActionButton
                              color="info"
                              onClick={() => handleOpenDetails(realocacao)}
                            >
                              <Visibility />
                            </ActionButton>
                          </Tooltip>

                          <Tooltip 
                            title={
                              realocacao.status === 'concluded' || realocacao.status === 'cancelled'
                                ? `Edição não disponível para realocações ${realocacao.status === 'concluded' ? 'concluídas' : 'canceladas'}`
                                : 'Editar realocação'
                            }
                          >
                            <span>
                              <ActionButton
                                color="primary"
                                onClick={() => handleEdit(realocacao)}
                                disabled={realocacao.status === 'concluded' || realocacao.status === 'cancelled'}
                              >
                                <Edit />
                              </ActionButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </Box>

            <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Página {pagination.currentPage} de {pagination.totalPages}
              </Typography>

              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                disabled={isLoading}
                showFirstButton
                showLastButton
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.875rem',
                    minWidth: 32,
                    height: 32,
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                  }
                }}
              />
            </Box>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%' }}
            elevation={6}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <RealocacaoDetailDialog
          open={detailOpen}
          onClose={handleCloseDetails}
          realocacao={selectedRealocacao}
          statusOptions={statusOptions}
          propertyTypes={propertyTypes}
          formatDate={formatDate}
        />
      </Container>
    </section>
  );
}